import {useEffect, useMemo, useState} from "react";
import {useHistory} from "react-router-dom";
import {DashboardLayout} from "components/Layout";
import {getInvoiceList} from "services/invoiceService";
import {InvoiceTable} from "components/invoice/InvoiceTable";

export default function InvoiceListPage() {
	const history = useHistory();
	const [searchInput, setSearchInput] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [invoices, setInvoices] = useState([]);
	const [meta, setMeta] = useState({
		currentPage: 1,
		totalItems: 0,
		totalPages: 0,
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		const timeout = setTimeout(() => {
			setDebouncedSearch(searchInput.trim());
			setPage(1);
		}, 300);

		return () => clearTimeout(timeout);
	}, [searchInput]);

	useEffect(() => {
		let isMounted = true;

		async function fetchInvoices() {
			setLoading(true);
			setError("");

			try {
				const response = await getInvoiceList({
					search: debouncedSearch || undefined,
					page,
					limit,
					sortBy: "createdAt.DESC",
				});

				if (!isMounted) {
					return;
				}

				const rows = response?.data?.rows || [];
				const currentPage = response?.data?.currentPage || page;
				const totalItems = response?.data?.totalItems || 0;
				const totalPages = response?.data?.totalPages || 0;

				setInvoices(rows);
				setMeta({
					currentPage,
					totalItems,
					totalPages,
				});
			} catch (apiError) {
				if (!isMounted) {
					return;
				}

				setError(apiError?.response?.data?.message || "Gagal memuat data invoice");
				setInvoices([]);
				setMeta({
					currentPage: 1,
					totalItems: 0,
					totalPages: 0,
				});
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		}

		fetchInvoices();

		return () => {
			isMounted = false;
		};
	}, [debouncedSearch, page, limit]);

	const tableData = useMemo(
		() =>
			invoices.map((item, index) => ({
				...item,
				idSerial: (meta.currentPage - 1) * limit + index + 1,
			})),
		[invoices, limit, meta.currentPage],
	);

	return (
		<DashboardLayout>
			<div className="text-bold text-dark mb-4">Invoice Management</div>

			<div className="box-white ws p15 mb-3">
				<div className="search position-relative">
					<input
						className="form-control"
						placeholder="Cari invoice number / pasien / rumah sakit"
						type="text"
						value={searchInput}
						onChange={event => setSearchInput(event.target.value)}
					/>
					<div className="search-icon">
						<span className="icon-ico-search" />
					</div>
				</div>
			</div>

			{error ? (
				<div className="alert alert-danger d-flex justify-content-between align-items-center">
					<span>{error}</span>
					<button className="btn btn-sm btn-outline-danger" type="button" onClick={() => setPage(1)}>
						Retry
					</button>
				</div>
			) : null}

			<InvoiceTable
				currentPage={meta.currentPage}
				data={tableData}
				limit={limit}
				loading={loading}
				totalItems={meta.totalItems}
				totalPages={meta.totalPages}
				onEdit={row => history.push(`/invoice-management/edit/${row?.id}`)}
				onLimitChange={nextLimit => {
					setLimit(nextLimit);
					setPage(1);
				}}
				onPageChange={nextPage => setPage(nextPage)}
			/>
		</DashboardLayout>
	);
}
